import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Search, X, TrendingUp, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_RPM = [0, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000];
const POWER_RATES = [1, 15, 40, 60, 80, 90, 95, 100, 90, 1];
const TORQUE_RATES = [1, 50, 90, 98.25, 100, 95, 85, 79, 66, 1];

const PowerChart = ({ vehicle, onClose }) => {
  const oriP = vehicle.power_ori || 0, oriT = vehicle.torque_ori || 0;
  const s1 = (vehicle.stages || []).find(s => s.stage === 1);
  const s1P = s1?.potencia_repro || 0, s1T = s1?.par_repro || 0;
  const data = useMemo(() => DEFAULT_RPM.map((r, i) => ({
    rpm: r,
    oemPower: Math.round((oriP * (POWER_RATES[i] || 0)) / 100),
    oemTorque: Math.round((oriT * (TORQUE_RATES[i] || 0)) / 100),
    ...(s1P > 0 ? { s1Power: Math.round((s1P * (POWER_RATES[i] || 0)) / 100), s1Torque: Math.round((s1T * (TORQUE_RATES[i] || 0)) / 100) } : {}),
  })).filter(d => d.rpm > 0), [oriP, oriT, s1P, s1T]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="bg-surface border border-white/10 rounded-lg w-full max-w-3xl p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        <h3 className="font-heading text-white font-bold text-lg mb-1">{vehicle.brand} {vehicle.model}</h3>
        <p className="text-zinc-400 text-sm mb-4">{vehicle.engine} | {vehicle.year_range}</p>
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-zinc-800 rounded px-3 py-2 text-center"><div className="text-zinc-500 text-[10px] uppercase">ORI</div><div className="text-white font-bold font-mono">{oriP}<span className="text-zinc-500 text-xs">hp</span></div></div>
          <div className="bg-zinc-800 rounded px-3 py-2 text-center"><div className="text-zinc-500 text-[10px] uppercase">Par ORI</div><div className="text-white font-bold font-mono">{oriT}<span className="text-zinc-500 text-xs">Nm</span></div></div>
          {s1P > 0 && <>
            <div className="bg-primary/10 border border-primary/20 rounded px-3 py-2 text-center"><div className="text-primary text-[10px] uppercase">Stage 1</div><div className="text-primary font-bold font-mono">{s1P}<span className="text-primary/60 text-xs">hp</span></div><div className="text-green-400 text-xs">+{s1.potencia_ganancia}</div></div>
            <div className="bg-primary/10 border border-primary/20 rounded px-3 py-2 text-center"><div className="text-primary text-[10px] uppercase">Par S1</div><div className="text-primary font-bold font-mono">{s1T}<span className="text-primary/60 text-xs">Nm</span></div><div className="text-green-400 text-xs">+{s1.par_ganancia}</div></div>
          </>}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="opG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#94a3b8" stopOpacity={0.12}/><stop offset="95%" stopColor="#94a3b8" stopOpacity={0}/></linearGradient>
              <linearGradient id="spG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.25}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="rpm" stroke="#475569" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${v/1000}k` : v} />
            <YAxis stroke="#334155" tick={{ fill: '#64748b', fontSize: 11 }} />
            <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', color: '#fff', fontSize: '12px' }} labelFormatter={v => `${v} RPM`} />
            <Legend />
            <Area type="monotone" dataKey="oemPower" name="OEM Power" stroke="#94a3b8" strokeWidth={1.5} fill="url(#opG)" dot={false} strokeDasharray="6 4" />
            <Area type="monotone" dataKey="oemTorque" name="OEM Torque" stroke="#64748b" strokeWidth={1.5} fill="none" dot={false} strokeDasharray="6 4" />
            {s1P > 0 && <Area type="monotone" dataKey="s1Power" name="Stage 1 Power" stroke="#ef4444" strokeWidth={2.5} fill="url(#spG)" dot={false} />}
            {s1P > 0 && <Area type="monotone" dataKey="s1Torque" name="Stage 1 Torque" stroke="#f97316" strokeWidth={2.5} fill="none" dot={false} />}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const VEHICLE_TYPES = [
  { value: '', label: 'Todos' },
  { value: 'turismo', label: 'Turismo' },
  { value: 'industrial', label: 'Industrial / Agricola' },
];

const VehicleDatabasePage = () => {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // Cascading filters
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [motors, setMotors] = useState([]);

  const [vehicleType, setVehicleType] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [motor, setMotor] = useState('');
  const [chartVehicle, setChartVehicle] = useState(null);

  // Load stats
  useEffect(() => {
    axios.get(`${API}/catalog/stats`).then(r => setTotal(r.data.total_vehicles || 0)).catch(() => {});
  }, []);

  // Load brands (on type change)
  useEffect(() => {
    axios.get(`${API}/catalog/brands`).then(r => setBrands(r.data.brands || [])).catch(() => {});
    setBrand(''); setModels([]); setModel(''); setYears([]); setYear(''); setMotors([]); setMotor('');
  }, [vehicleType]);

  // Load models on brand change
  useEffect(() => {
    if (!brand) { setModels([]); setModel(''); setYears([]); setYear(''); setMotors([]); setMotor(''); return; }
    axios.get(`${API}/catalog/models?brand=${encodeURIComponent(brand)}`).then(r => setModels(r.data.models || [])).catch(() => {});
    setModel(''); setYears([]); setYear(''); setMotors([]); setMotor('');
  }, [brand]);

  // Load years on model change
  useEffect(() => {
    if (!brand || !model) { setYears([]); setYear(''); setMotors([]); setMotor(''); return; }
    axios.get(`${API}/catalog/years?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}`).then(r => setYears(r.data.years || [])).catch(() => {});
    setYear(''); setMotors([]); setMotor('');
  }, [brand, model]);

  // Load motors on year change
  useEffect(() => {
    if (!brand || !model) { setMotors([]); setMotor(''); return; }
    const params = new URLSearchParams({ brand, model });
    if (year) params.append('year', year);
    axios.get(`${API}/catalog/engines?${params}`).then(r => {
      const engineList = (r.data.engines || []).map(e => e.engine);
      setMotors([...new Set(engineList)]);
    }).catch(() => {});
    setMotor('');
  }, [brand, model, year]);

  // Auto-search when filters change
  useEffect(() => {
    if (brand) doSearch();
  }, [brand, model, year, motor]);

  const doSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (brand) params.append('brand', brand);
      if (model) params.append('model', model);
      if (motor) params.append('q', motor);
      params.append('limit', '200');
      const res = await axios.get(`${API}/catalog/search?${params}`);
      let results = res.data.vehicles || [];
      // Client-side filter by year if selected
      if (year) results = results.filter(v => v.year_range === year);
      // Client-side filter by motor if selected
      if (motor) results = results.filter(v => v.engine === motor);
      setVehicles(results);
    } catch {}
    setLoading(false);
  };

  const handleSearch = (e) => { e.preventDefault(); doSearch(); };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="vehicle-database-page">
        <h1 className="font-heading text-3xl font-bold text-white mb-1">BASE DE DATOS VEHICULOS</h1>
        <p className="text-zinc-500 text-sm mb-6">{total.toLocaleString()} vehiculos disponibles</p>

        {/* Filters — Tipo → Marca → Modelo → Año → Motor */}
        <form onSubmit={handleSearch} className="bg-surface border border-white/10 rounded-md p-4 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1 block">TIPO VEHICULO</label>
              <select value={vehicleType} onChange={e => setVehicleType(e.target.value)}
                className="w-full bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm" data-testid="filter-type">
                {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1 block">MARCA</label>
              <select value={brand} onChange={e => setBrand(e.target.value)}
                className="w-full bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm" data-testid="filter-brand">
                <option value="">Seleccionar...</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1 block">MODELO</label>
              <select value={model} onChange={e => setModel(e.target.value)} disabled={!brand}
                className="w-full bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="filter-model">
                <option value="">Todos</option>
                {models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1 block">AÑO</label>
              <select value={year} onChange={e => setYear(e.target.value)} disabled={!model}
                className="w-full bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="filter-year">
                <option value="">Todos</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1 block">MOTOR</label>
              <select value={motor} onChange={e => setMotor(e.target.value)} disabled={!model}
                className="w-full bg-surface-highlight border border-white/10 rounded px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="filter-motor">
                <option value="">Todos</option>
                {motors.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <Button type="submit" className="bg-primary hover:bg-primary/90 text-white w-full h-[38px]" data-testid="filter-search">
                <Search className="w-4 h-4 mr-2" />BUSCAR
              </Button>
            </div>
          </div>
        </form>

        {vehicles.length > 0 && <p className="text-zinc-500 text-sm mb-3 border-b border-white/5 pb-2">{vehicles.length} resultados</p>}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">{brand ? 'Sin resultados' : 'Selecciona una marca para empezar'}</p>
          </div>
        ) : (
          <div className="bg-surface border border-white/10 rounded-md overflow-x-auto">
            <table className="w-full" data-testid="vehicles-table">
              <thead>
                <tr className="border-b border-white/10 text-[10px] text-zinc-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-3">VEHICULO</th>
                  <th className="text-right px-3 py-3">CV</th>
                  <th className="text-right px-3 py-3">PAR</th>
                  <th className="text-center px-3 py-3">COMB.</th>
                  <th className="text-left px-3 py-3">MOTOR</th>
                  <th className="text-center px-3 py-3">STAGE 1</th>
                  <th className="text-center px-3 py-3">CURVA</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, i) => {
                  const s1 = (v.stages || []).find(s => s.stage === 1);
                  const eng = (v.engine || '').toLowerCase();
                  const isDiesel = eng.includes('d') || eng.includes('tdi') || eng.includes('hdi') || eng.includes('cdti') || eng.includes('dci') || eng.includes('bluehdi') || eng.includes('jtd');
                  return (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-white text-sm">{v.brand} {v.model} {v.engine}</p>
                        <p className="text-zinc-600 text-xs">{v.year_range}</p>
                      </td>
                      <td className="text-right px-3 py-3 text-white text-sm font-mono">{v.power_ori || '-'}<span className="text-zinc-600 text-xs">hp</span></td>
                      <td className="text-right px-3 py-3 text-white text-sm font-mono">{v.torque_ori || s1?.par_original || '-'}<span className="text-zinc-600 text-xs">Nm</span></td>
                      <td className="text-center px-3 py-3">
                        <span className={`text-[10px] px-2 py-1 rounded-sm font-medium ${isDiesel ? 'bg-blue-500/15 text-blue-400' : 'bg-green-500/15 text-green-400'}`}>
                          {isDiesel ? 'Diesel' : 'Petrol'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-zinc-400 text-xs">{v.engine}</td>
                      <td className="text-center px-3 py-3">
                        {s1 ? (
                          <span className="text-sm"><span className="text-primary font-mono font-bold">{s1.potencia_repro}hp</span><span className="text-green-400 text-xs ml-1">+{s1.potencia_ganancia}</span></span>
                        ) : <span className="text-zinc-600 text-xs">-</span>}
                      </td>
                      <td className="text-center px-3 py-3">
                        {s1 && <button onClick={() => setChartVehicle(v)} className="text-primary text-xs hover:underline flex items-center gap-1 mx-auto" data-testid={`chart-${i}`}><TrendingUp className="w-3 h-3" />Ver curva</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {chartVehicle && <PowerChart vehicle={chartVehicle} onClose={() => setChartVehicle(null)} />}
      </div>
    </Layout>
  );
};

export default VehicleDatabasePage;
